import { getReaderChapterVoice, type ReaderBook, type ReaderChapter } from '@/services/booksApi';
import { listChapterAssets, type ChapterAssetRef } from '@/utils/chapterAssets';
import { isUnmetered, type NetworkState } from '@/utils/networkState';

/**
 * The buffer's policy, as pure functions: given a book, a voice, where the
 * playhead is and how far ahead to look, what should be on disk and in what
 * order. Nothing here touches the network, the disk or a clock, which is what
 * makes the whole policy unit-testable and the scheduler around it thin.
 *
 * Two depths, because the numbers are so different: text, captions, CSS and
 * images come to a couple of hundred kilobytes a chapter and are fetched for
 * the whole book; audio is megabytes a chapter and is fetched only for a
 * rolling window ahead of the playhead. Losing signal then degrades from
 * "narrated" to "readable" rather than from "working" to "stopped".
 */

/** Ninety minutes: every commute-scale gap, at roughly 130 MB of audio. */
export const LOOK_AHEAD_UNMETERED_MS = 90 * 60_000;

/**
 * Fifteen minutes on cellular: close to what an ordinary streaming player
 * consumes anyway, which is what makes fetching it unprompted defensible.
 */
export const LOOK_AHEAD_METERED_MS = 15 * 60_000;

/** How far ahead of the playhead to keep audio on disk, for a connection. */
export const lookAheadWindowMs = (network: NetworkState): number => {
  if (!network.isOnline) {
    return 0;
  }
  return isUnmetered(network) ? LOOK_AHEAD_UNMETERED_MS : LOOK_AHEAD_METERED_MS;
};

/**
 * Why a fetch is in the plan, in priority order. The current chapter first,
 * because losing signal now is the case that matters most; then the audio
 * window; then the rest of the book's text, forward before backward.
 */
export type BufferTier = 'current' | 'ahead' | 'text-ahead' | 'text-behind';

export interface PlannedFetch {
  ref: ChapterAssetRef;
  chapterId: string;
  chapterIndex: number;
  tier: BufferTier;
}

/** The slice of a reader payload the policy needs. */
export interface BufferBook {
  ebook: Pick<ReaderBook['ebook'], 'cssUrls'>;
  chapters: ReaderChapter[];
}

export interface BufferPlanInput {
  book: BufferBook;
  voiceKey: string;
  currentChapterIndex: number;
  /** Playhead within the current chapter. */
  positionInChapterMs: number;
  windowMs: number;
  /**
   * Fetch the whole book's text, not only the window's chapters. Off on a
   * metered connection, where a long book's text is comparable in size to
   * the audio window itself.
   */
  eagerText: boolean;
  /**
   * Media found in chapter HTML that has already been fetched, by chapter
   * id. The payload does not enumerate images, so they are discovered as the
   * HTML lands and join the plan at their chapter's priority.
   */
  discoveredMedia: ReadonlyMap<string, readonly string[]>;
  isHeld: (cdnUrl: string) => boolean;
}

const audioDurationMs = (chapter: ReaderChapter, voiceKey: string): number =>
  getReaderChapterVoice(chapter, voiceKey)?.durationMs ?? 0;

const clampIndex = (index: number, length: number): number =>
  Math.min(Math.max(0, Math.floor(index)), Math.max(0, length - 1));

/**
 * What to fetch, in order, skipping anything already held. Cheap enough to
 * recompute before every fetch, which is how a playhead jump or a network
 * change is absorbed without any cancellation logic: the next plan simply
 * starts from wherever the listener now is.
 */
export const planBufferFetches = ({
  book,
  voiceKey,
  currentChapterIndex,
  positionInChapterMs,
  windowMs,
  eagerText,
  discoveredMedia,
  isHeld,
}: BufferPlanInput): PlannedFetch[] => {
  const { chapters } = book;
  if (chapters.length === 0 || !voiceKey) {
    return [];
  }

  const plan: PlannedFetch[] = [];
  const seen = new Set<string>();
  const add = (ref: ChapterAssetRef, chapter: ReaderChapter, chapterIndex: number, tier: BufferTier) => {
    if (seen.has(ref.cdnUrl) || isHeld(ref.cdnUrl)) {
      return;
    }
    seen.add(ref.cdnUrl);
    plan.push({ ref, chapterId: chapter.id, chapterIndex, tier });
  };
  const addChapter = (chapterIndex: number, tier: BufferTier, includeAudio: boolean) => {
    const chapter = chapters[chapterIndex];
    for (const ref of listChapterAssets(book, chapter, voiceKey)) {
      if (ref.kind === 'audio' && !includeAudio) {
        continue;
      }
      add(ref, chapter, chapterIndex, tier);
    }
    for (const cdnUrl of discoveredMedia.get(chapter.id) ?? []) {
      add({ kind: 'image', cdnUrl, voiceKey: null, expectedBytes: null }, chapter, chapterIndex, tier);
    }
  };

  const current = clampIndex(currentChapterIndex, chapters.length);
  addChapter(current, 'current', true);

  // The window is measured in listening time from the playhead, and a
  // chapter is atomic: the one that crosses the edge is included whole.
  let aheadMs = Math.max(0, audioDurationMs(chapters[current], voiceKey) - Math.max(0, positionInChapterMs));
  let next = current + 1;
  while (next < chapters.length && aheadMs < windowMs) {
    addChapter(next, 'ahead', true);
    aheadMs += audioDurationMs(chapters[next], voiceKey);
    next += 1;
  }

  if (eagerText) {
    for (let index = next; index < chapters.length; index += 1) {
      addChapter(index, 'text-ahead', false);
    }
    for (let index = current - 1; index >= 0; index -= 1) {
      addChapter(index, 'text-behind', false);
    }
  }

  return plan;
};

export interface BufferDepthInput {
  book: Pick<BufferBook, 'chapters'>;
  voiceKey: string;
  currentChapterIndex: number;
  positionInChapterMs: number;
  isHeld: (cdnUrl: string) => boolean;
}

/**
 * How much listening time is on disk ahead of the playhead, contiguously:
 * the rest of the current chapter if its audio is held, then each following
 * chapter until one is missing. This is the buffer's depth, kept for logs
 * and the dev card only — by design there is no indicator in the reader.
 */
export const bufferedAheadMs = ({
  book,
  voiceKey,
  currentChapterIndex,
  positionInChapterMs,
  isHeld,
}: BufferDepthInput): number => {
  const { chapters } = book;
  if (chapters.length === 0 || !voiceKey) {
    return 0;
  }
  const current = clampIndex(currentChapterIndex, chapters.length);
  let depth = 0;
  for (let index = current; index < chapters.length; index += 1) {
    const voice = getReaderChapterVoice(chapters[index], voiceKey);
    if (!voice) {
      // A chapter with no narration is read, not played; it neither adds
      // time nor breaks the run.
      continue;
    }
    if (!isHeld(voice.audioUrl)) {
      break;
    }
    depth += index === current ? Math.max(0, voice.durationMs - Math.max(0, positionInChapterMs)) : voice.durationMs;
  }
  return depth;
};
